{/*
  * ORTBBidResponsePage.tsx
  * https://dash.applovin.com/documentation/mediation/auctions/dsps/ortb-spec/bid-response
  * Documentation > MAX Demand Partners > DSPs > ORTB Specification > Bid Response
  */}
import React, { FunctionComponent } from 'react';
import { DataTable, DocsPageModule, LabelFontFamily } from 'UI-Dash-Commons-UI';
import CodeSnippet from '../containers/CodeSnippetContainer';
import styled from 'styled-components';
import {
    bidObject,
    bidResponseObject,
    rtbMacros,
    seatbidObject,
    trackingFields,
} from '../redux/constants';

type ORTBBidResponsePageProps = {
    linkTargetTopPosition?: number;
}

const ORTBBidResponsePage: FunctionComponent<ORTBBidResponsePageProps> = ( { linkTargetTopPosition } ) =>
{
    return (
        <>
            <DocsPageModule.DocsPageHeader text={'Bid Response'} type={DocsPageModule.DocsPageHeaderType.HEADING_1}
                                           linkTargetTopPosition={linkTargetTopPosition}/>
            <DocsPageModule.DocsPageHeader text={'Size'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'The maximum size of the bid response is 4KB.'}/>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'No Bids'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'To communicate a no-bid response, pass an empty response with a status code of 204.'}/>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Win/Loss/Billing Notifications'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'AppLovin RTB calls the win, loss, and billing notice URLs that the requester supplies in the request’s '}/>
                <DocsPageModule.DocsPageContentCode children={'nurl'}/>
                <DocsPageModule.DocsPageContentText text={', '}/>
                <DocsPageModule.DocsPageContentCode children={'lurl'}/>
                <DocsPageModule.DocsPageContentText text={', and '}/>
                <DocsPageModule.DocsPageContentCode children={'burl'}/>
                <DocsPageModule.DocsPageContentText
                    text={' fields respectively. '
                    + 'AppLovin RTB calls the win URL when the auction completes and AppLovin sends the ad to the device. '
                    + 'It calls the loss url either during the auction, if the associated ad fails to load properly, or after a different winning ad loads. '
                    + 'It calls the billing URL when the device renders the ad, that is, when the video starts playing, or, for graphic ads, when the device displays the ad.'}
                />
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'The requester may embed macros into the specification of these notification URLs. '
                        + 'AppLovin RTB will substitute appropriate values for those macros in the URL before AppLovin RTB requests the URL. '
                        + 'The following list shows the macros that the requester may embed in these URLs:'}/>
            </DocsPageModule.DocsPageContent>

            <DataTable
                data={rtbMacros}
                columns={
                    [
                        {
                            id: 'macro',
                            Header: 'Macro',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.macro}/>
                        },
                        {
                            id: 'description',
                            Header: 'Description',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.description}/>
                        }
                    ]
                }
                totalRowCount={rtbMacros.length}
            />

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'These macros are case-sensitive; specify them in ALL CAPS. '
                    + 'Enclose these macros in '}/>
                <DocsPageModule.DocsPageContentCode children={'${…}'}/>
                <DocsPageModule.DocsPageContentText text={' (e.g. '}/>
                <DocsPageModule.DocsPageContentCode children={'${AUCTION_ID}'}/>
                <DocsPageModule.DocsPageContentText text={').'} />
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Example Win Bid URL with Macros'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_3}
                                           linkTargetTopPosition={linkTargetTopPosition}/>
            <CodeSnippet
                snippets={[ {
                    title: 'text',
                    language: 'plaintext',
                    code: `https://example.com/endpoint/auction=\${AUCTION_ID}&price=\${AUCTION_PRICE}`
                } ]}
            />

            <DocsPageModule.DocsPageHeader text={'Ad Markup Standard'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'DSP bidders must include ad markup in the bid response. '
                    + 'AppLovin RTB does not support returning the ad markup in response to the win notice.'}/>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'Your ad markup must comply with AppLovin’s RTB ad markup standard. '
                    + 'AppLovin’s RTB servers check the ad markup’s compliance as soon as they receive the bid, and then they perform the following sanity checks on the ad markup:'}/>
                <DocsPageModule.DocsPageUnorderedList>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'Ad markup is properly escaped.'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'Ad markup is URL-encoded, except for VAST ads.'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'HTML of the ad markup is a snippet.'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText
                            text={'When you bid with a rich media ad, put the content of the node in a CDATA construct. '
                            + 'CDATA in XML requires no encoding whatsoever. '
                            + 'However if you pass an XML document in a JSON-context, you must apply JSON syntax and escaping rules, as follows:'}/>
                        <DocsPageModule.DocsPageUnorderedList>
                            <DocsPageModule.DocsPageBulletPoint>
                                <DocsPageModule.DocsPageContentText text={'All double quotes are escaped as '}/>
                                <DocsPageModule.DocsPageContentCode children={'\\"'}/>
                                <DocsPageModule.DocsPageContentText text={'.'}/>
                            </DocsPageModule.DocsPageBulletPoint>
                            <DocsPageModule.DocsPageBulletPoint>
                                <DocsPageModule.DocsPageContentText text={'Apostrophes are not escaped.'}/>
                            </DocsPageModule.DocsPageBulletPoint>
                            <DocsPageModule.DocsPageBulletPoint>
                                <DocsPageModule.DocsPageContentText
                                    text={'The XML doc is stripped of all tab and newline characters. ('}/>
                                <DocsPageModule.DocsPageContentAnchor
                                    to={'/documentation/mediation/auctions/dsps/ortb-spec/creative-types#vast-support'}
                                    text={'The Example VAST Ad Markup'}
                                    newWindow={false}
                                />
                                <DocsPageModule.DocsPageContentText
                                    text={' is formatted for easy readability. '
                                    + 'Do not send extra whitespace in your actual ad markup.)'}/>
                            </DocsPageModule.DocsPageBulletPoint>
                        </DocsPageModule.DocsPageUnorderedList>
                    </DocsPageModule.DocsPageBulletPoint>
                </DocsPageModule.DocsPageUnorderedList>

                <DocsPageModule.DocsPageContentText
                    text={'To verify that an ad markup snippet is valid JSON content (along with its CDATA), you can pass that snippet into '}/>
                <DocsPageModule.DocsPageContentAnchor
                    to={'https://jsonlint.com/'}
                    text={'the on-line JSONLint validator'}
                    newWindow={true}
                />
                <DocsPageModule.DocsPageContentText text={'.'}/>
            </DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'SDK bidders are not required to pass ad markup because all creatives should be rendered via your SDK. '
                        + 'SDK bidders may use the '} />
                <DocsPageModule.DocsPageContentCode children={'admarkup'} />
                <DocsPageModule.DocsPageContentText
                    text={' field to pass custom data, which can be configured to be returned to your SDK at load time via the adapters. '} />
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Bid Response Components'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'There are three components to the bid response:'}/>
                <DocsPageModule.DocsPageOrderedList>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentAnchor
                            to={'#bidresponse-object'}
                            text={'bidresponse'}
                            newWindow={false}
                        />
                        <DocsPageModule.DocsPageContentText text={' — top-level object'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentAnchor
                            to={'#seatbid-object'}
                            text={'seatbid'}
                            newWindow={false}
                        />
                        <DocsPageModule.DocsPageContentText text={' — collection of bids made by the bidder on behalf of a specific seat'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentAnchor
                            to={'#bid-object'}
                            text={'bid'}
                            newWindow={false}
                        />
                        <DocsPageModule.DocsPageContentText text={' — an offer to buy a specific impression under certain business terms'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                </DocsPageModule.DocsPageOrderedList>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Bid Response Object'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>
            <DataTable
                data={bidResponseObject}
                columns={
                    [
                        {
                            id: 'attribute',
                            Header: 'Attribute',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.attribute}/>
                        },
                        {
                            id: 'type',
                            Header: 'Type',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.type}/>
                        },
                        {
                            id: 'value',
                            Header: 'Description',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.value}/>
                        },
                        {
                            id: 'required',
                            Header: 'Required?',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.required}/>
                        },
                    ]
                }
                totalRowCount={bidResponseObject.length}
            />

            <DocsPageModule.DocsPageHeader text={'Seatbid Object'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>
            <DataTable
                data={seatbidObject}
                columns={
                    [
                        {
                            id: 'attribute',
                            Header: 'Attribute',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.attribute}/>
                        },
                        {
                            id: 'type',
                            Header: 'Type',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.type}/>
                        },
                        {
                            id: 'value',
                            Header: 'Description',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.value}/>
                        },
                        {
                            id: 'required',
                            Header: 'Required?',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.required}/>
                        },
                    ]
                }
                totalRowCount={seatbidObject.length}
            />

            <DocsPageModule.DocsPageHeader text={'Bid Object'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>
            <DataTable
                data={bidObject}
                columns={
                    [
                        {
                            id: 'attribute',
                            Header: 'Attribute',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.attribute}/>
                        },
                        {
                            id: 'type',
                            Header: 'Type',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.type}/>
                        },
                        {
                            id: 'value',
                            Header: 'Description',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.value}/>
                        },
                        {
                            id: 'required',
                            Header: 'Required?',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.required}/>
                        },
                    ]
                }
                totalRowCount={bidObject.length}
            />

            <DocsPageModule.DocsPageHeader text={'Tracking Fields'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>
            <DocsPageModule.DocsPageContent
                text={`The following table shows the tracking fields available to Demand Side Platforms, and their recommended uses.`}
            />
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={`Note: `} fontFamily={LabelFontFamily.FONT_FAMILY_DEMI} />
                <DocsPageModule.DocsPageContentText text={`Impressions and spend tracking must be done through the bid response field `} />
                <DocsPageModule.DocsPageContentCode children={`burl`} />
                <DocsPageModule.DocsPageContentText text={`. AppLovin will only investigate discrepancies when `} />
                <DocsPageModule.DocsPageContentCode children={`burl`} />
                <DocsPageModule.DocsPageContentText text={` is used for tracking.`} />
            </DocsPageModule.DocsPageContent>
            <DataTable
                data={trackingFields}
                columns={
                    [
                        {
                            id: 'tracker',
                            Header: 'Tracker',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.tracker}/>
                        },
                        {
                            id: 'method',
                            Header: 'Method',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.method}/>
                        },
                        {
                            id: 'recommendeduse',
                            Header: 'Recommended Use',
                            sortable: false,
                            Cell: props => <DescriptionCellWrapper text={props.original.recommendeduse}/>
                        }
                    ]
                }
                totalRowCount={trackingFields.length}
            />

            <DocsPageModule.DocsPageHeader text={'Example Bid Response'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <CodeSnippet
                snippets={[ {
                    title: 'text',
                    language: 'json',
                    code: `{
  "id": "5e1eb292d6b7ea9cf3da74ddb385996a62d3a6e9",
  "seatbid": [
    {
      "bid": [
        {
          "id": "8JxH4DHN4KMF21Vd",
          "impid": "1",
          "price": 15,
          "adid": "1093919",
          "adm": "<a href=\\"https://click.url/click/8JxH4DHN4KMF21Vd?uid=05D02DC6-0132-4C2C-A879-79BB026BE3F8&partner=applovin&ts=2018-07-09T18-19-01Z&ad=1093919\\"><img src=\\"https://assets.dsp.io/ad_assets/files/320x50.png\\" height=\\"50\\" width=\\"320\\" alt=\\"\\"/></a>",
          "adomain": [
            "advertiser.com"
          ],
          "bundle": "123123123",
          "iurl": "https://assets.dsp.io/ad_assets/files/320x50.png",
          "cid": "12345",
          "crid": "crid123",
          "cat": [
            "IAB22-2"
          ],
          "h": 50,
          "w": 320,
          "ext": {
            "crtype": "HTML",
            "imptrackers": [
                "https://example.dsp.events/win/8JxH4DHN4KMF21Vd?ts=2018-07-09T18-19-01Z&ad=1093919&uid=05D02DC6-0132-4C2C-A879-79BB026BE3F8&auction=\${AUCTION_ID}&price=\${AUCTION_PRICE}&partner=applovin"
            ]
          }
        }
      ]
    }
  ],
  "bidid": "8JxH4DHN4KMF21Vd",
  "cur": "USD"
}
`
                }
                ]
                }
            />

            <DocsPageModule.DocsPageHeader text={'Example Native Ad Response'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>

            <CodeSnippet
                snippets={[ {
                    title: 'text',
                    language: 'json',
                    code: `{
   "bidid":"9aa2a2950894c95b9b02476a5ba5438dc6de8dc1",
   "cur":"USD",
   "id":"65de6af36e6fb32778afa94a996ec4c2b514145d",
   "seatbid":[
      {
         "bid":[
             {
               "adm":"{\\"native\\":{\\"assets\\":[{\\"id\\":100,\\"title\\":{\\"text\\":\\"Test Ad\\"}},{\\"id\\":200},{\\"id\\":300,\\"img\\":{\\"h\\":null,\\"url\\":\\"https://assets.com/main_image.png_\\",\\"w\\":null}},{\\"id\\":310,\\"img\\":{\\"h\\":80,\\"url\\":\\"https://assets.com/icon_image.png\\",\\"w\\":80}},{\\"data\\":{\\"value\\":\\"Test Ad Subline\\"},\\"id\\":420},{\\"data\\":{\\"value\\":\\"Continue\\"},\\"id\\":430},{\\"data\\":{\\"value\\":\\"4.5\\"},\\"id\\":410}],\\"imptrackers\\":[\\"https://adserver.com/impression_tracker\\"],\\"link\\":{\\"url\\":\\"https://adserver.com/click_destination\\"}}}\\n",
               "adomain":[
                  "somegame.com"
               ],
               "bundle":"com.game.example",
               "cat":[
                  "IAB1"
               ],
               "crid":"74650.0!3ILDTypkALKe8xfw2WJLmxfNOftiOg2u3yeGIKEULAU*",
               "ext": {
                  "crtype":"native"
               },
               "id": "1",
               "impid": "1",
               "iurl": "https://assets.com/preview.png",
               "price": 1.0
            }
         ],
         "seat":"A00000001"
      }
   ]
}`
                }
                ]
                }
            />

        </>
    )
}

const DescriptionCellWrapper = styled( DocsPageModule.DocsPageContent )`
  margin: 0;
  font-size: inherit;
  line-height: inherit;
  white-space: normal;
`;

export default ORTBBidResponsePage;
